import React, { useCallback, useMemo, useState } from 'react';
import { ArrowPathIcon, ExclamationTriangleIcon } from '@heroicons/react/24/outline';
import type { KpiData } from './types';
import type { KpiDrilldownSpec, KpiHeatmapCustomerApiData } from './kpiDashboardApi';
import { formatKpiTwoDecimalDisplay } from './kpiDashboardNumberFormat';
import { SectionLabel } from './KpiDashboardContent';
import KpiCustomerHeatmapTable from './KpiCustomerHeatmapTable';
import {
  KpiBacklogBarChart,
  KpiPrioritySlaChart,
} from './charts';
import KpiIncidentCommComplianceDonuts from './charts/KpiIncidentCommComplianceDonuts';
import KpiIncidentsWeeklyLineChart from './charts/KpiIncidentsWeeklyLineChart';
import KpiIncidentAssignmentGroupComboChart from './charts/KpiIncidentAssignmentGroupComboChart';

export interface KpiIncidentDashboardTabProps {
  chartsData: KpiData | undefined;
  chartsLoading: boolean;
  chartsFetching: boolean;
  chartsError: Error | null;
  chartsOnRetry?: () => void;
  heatmapData: KpiHeatmapCustomerApiData | undefined;
  heatmapLoading: boolean;
  heatmapFetching: boolean;
  heatmapError: Error | null;
  heatmapOnRetry?: () => void;
  onDrill: (spec: KpiDrilldownSpec) => void;
}

function IncidentKpiHeaderStrip({
  incidentsCount,
  avgOpenDays,
  mttr,
  onMetric,
}: {
  incidentsCount: string;
  avgOpenDays: string;
  mttr: string;
  onMetric: (key: string) => void;
}) {
  const items = [
    { label: 'Incidents Count KPI', value: incidentsCount, drillKey: 'incidents_volume' as const },
    { label: 'Incidents Avg Open Days', value: avgOpenDays, drillKey: 'avg_open_days' as const },
    { label: 'MTTR (Minutes)', value: mttr, drillKey: 'mttr' as const },
  ];

  return (
    <div className="overflow-hidden rounded-xl border border-slate-200/90 bg-white shadow-sm shadow-slate-900/5 ring-1 ring-slate-950/[0.04]">
      <div className="h-1 bg-gradient-to-r from-[#2F5597] via-[#4472C4] to-sky-500" aria-hidden />
      <div className="grid grid-cols-1 divide-y divide-slate-100 sm:grid-cols-3 sm:divide-x sm:divide-y-0">
        {items.map((item, i) => (
          <button
            key={item.label}
            type="button"
            onClick={() => onMetric(item.drillKey)}
            className="flex flex-col items-start px-6 py-5 text-left transition-colors hover:bg-slate-50/90 focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#4472C4]"
          >
            <span className="text-xs font-medium uppercase tracking-wide text-slate-500">{item.label}</span>
            <span
              className={`mt-1 text-3xl font-bold tabular-nums tracking-tight text-slate-900 ${
                i === 0 ? 'underline decoration-slate-900 decoration-1 underline-offset-4' : ''
              }`}
            >
              {item.value}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

const KpiIncidentDashboardTab: React.FC<KpiIncidentDashboardTabProps> = ({
  chartsData,
  chartsLoading,
  chartsFetching,
  chartsError,
  chartsOnRetry,
  heatmapData,
  heatmapLoading,
  heatmapFetching,
  heatmapError,
  heatmapOnRetry,
  onDrill,
}) => {
  const [customerSortDesc, setCustomerSortDesc] = useState(false);

  const openMetric = useCallback(
    (key: string) => {
      if (key === 'mttr') onDrill({ kind: 'metric', key: 'mttr' });
      else if (key === 'incidents_volume') onDrill({ kind: 'metric', key: 'incidents_volume' });
      else if (key === 'avg_open_days') onDrill({ kind: 'metric', key: 'avg_open_days' });
    },
    [onDrill]
  );

  const openRate = useCallback(
    (key: string) => {
      onDrill({ kind: 'rate', key });
    },
    [onDrill]
  );

  const openPriority = useCallback(
    (priority: string) => {
      onDrill({ kind: 'priority', priority });
    },
    [onDrill]
  );

  const openBacklog = useCallback(
    (bucketKey: string) => {
      onDrill({ kind: 'backlog', bucket: bucketKey });
    },
    [onDrill]
  );

  const openAssignmentGroup = useCallback(
    (groupName: string) => {
      onDrill({ kind: 'assignment_group', groupName, ticketType: 'INCIDENT' });
    },
    [onDrill]
  );

  const sortedRows = useMemo(() => {
    if (!heatmapData?.heatmap.rows.length) return [];
    const rows = [...heatmapData.heatmap.rows];
    rows.sort((a, b) => {
      const cmp = a.customerName.localeCompare(b.customerName, undefined, { sensitivity: 'base' });
      return customerSortDesc ? -cmp : cmp;
    });
    return rows;
  }, [heatmapData?.heatmap.rows, customerSortDesc]);

  const loading = (chartsLoading && !chartsData) || (heatmapLoading && !heatmapData);
  if (loading) {
    return (
      <div className="space-y-8" aria-busy="true" aria-label="Loading incident dashboard">
        <div className="h-24 animate-pulse rounded-xl bg-slate-200/80" />
        <div className="grid gap-4 lg:grid-cols-12">
          <div className="h-72 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-8" />
          <div className="h-72 animate-pulse rounded-xl bg-slate-200/80 lg:col-span-4" />
        </div>
        <div className="grid gap-4 lg:grid-cols-2">
          <div className="h-72 animate-pulse rounded-xl bg-slate-200/80" />
          <div className="h-72 animate-pulse rounded-xl bg-slate-200/80" />
        </div>
      </div>
    );
  }

  const blockingError = chartsError && !chartsData;
  const heatmapBlocking = heatmapError && !heatmapData;
  if (blockingError || heatmapBlocking) {
    const err = chartsError || heatmapError;
    return (
      <div className="rounded-xl border border-red-200/80 bg-white p-8 text-center shadow-sm">
        <ExclamationTriangleIcon className="mx-auto h-10 w-10 text-red-500" aria-hidden />
        <p className="mt-3 text-sm font-semibold text-red-900">Could not load incident dashboard</p>
        <p className="mt-1 text-sm text-red-700/90">{err?.message}</p>
        {(chartsOnRetry || heatmapOnRetry) && (
          <button
            type="button"
            onClick={() => {
              chartsOnRetry?.();
              heatmapOnRetry?.();
            }}
            className="mt-4 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-700"
          >
            Try again
          </button>
        )}
      </div>
    );
  }

  if (!chartsData || !heatmapData) return null;

  const { heatmap, volumeByWeek } = heatmapData;
  const maxH = heatmap.maxCount > 0 ? heatmap.maxCount : 1;

  const ongoingRate = chartsData.incidentOngoingSlaMetRate;
  const totalSla = chartsData.incidentOngoingSlaTicketCount;

  const resolutionRate = chartsData.incidentResolutionSlaMetRate;
  const totalResolutionSla = chartsData.incidentResolutionSlaTicketCount;
  const resolutionRateNum = parseFloat(resolutionRate);
  const resolutionCompliant =
    Number.isFinite(resolutionRateNum) && totalResolutionSla > 0
      ? Math.round((totalResolutionSla * resolutionRateNum) / 100)
      : 0;
  const resolutionNonCompliant = Math.max(0, totalResolutionSla - resolutionCompliant);

  const ongoingRateNum = parseFloat(ongoingRate);
  const ongoingCompliant =
    Number.isFinite(ongoingRateNum) && totalSla > 0 ? Math.round((totalSla * ongoingRateNum) / 100) : 0;
  const ongoingNonCompliant = Math.max(0, totalSla - ongoingCompliant);

  const incidentsMttr = chartsData.incidentsMttrMinutes !== '—' ? chartsData.incidentsMttrMinutes : chartsData.mttrMinutes;
  const incidentsAvgOpen =
    chartsData.incidentsAvgOpenDays !== '—' ? chartsData.incidentsAvgOpenDays : chartsData.ticketsAvgOpenDays;

  return (
    <div className="relative">
      {(chartsFetching || heatmapFetching) && (
        <div
          className="pointer-events-none absolute right-0 top-0 z-10 flex items-center gap-1.5 rounded-full border border-indigo-200/80 bg-white/95 px-3 py-1.5 text-xs font-medium text-indigo-800 shadow-md shadow-indigo-900/10 backdrop-blur-sm"
          aria-live="polite"
        >
          <ArrowPathIcon className="h-3.5 w-3.5 animate-spin text-indigo-600" aria-hidden />
          Updating
        </div>
      )}
      {(chartsError || heatmapError) && chartsData && heatmapData && (
        <div className="mb-4 flex flex-wrap items-center gap-2 rounded-xl border border-amber-200/90 bg-amber-50/90 px-4 py-3 text-sm text-amber-950 shadow-sm">
          <ExclamationTriangleIcon className="h-5 w-5 shrink-0 text-amber-600" aria-hidden />
          <span className="min-w-0 flex-1">
            Showing cached data. Latest refresh failed: {(chartsError || heatmapError)?.message}
          </span>
          <button
            type="button"
            onClick={() => {
              chartsOnRetry?.();
              heatmapOnRetry?.();
            }}
            className="shrink-0 font-semibold text-amber-900 underline decoration-amber-600/50 underline-offset-2 hover:text-amber-950"
          >
            Retry
          </button>
        </div>
      )}

      <section className="mb-6">
        <SectionLabel>Incident KPIs</SectionLabel>
        <IncidentKpiHeaderStrip
          incidentsCount={chartsData.incidentsVolume.toLocaleString()}
          avgOpenDays={formatKpiTwoDecimalDisplay(incidentsAvgOpen)}
          mttr={formatKpiTwoDecimalDisplay(incidentsMttr)}
          onMetric={openMetric}
        />
      </section>

      <section className="mb-6">
        <SectionLabel>Customer &amp; SLA</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-12">
          <div className="min-h-0 lg:col-span-8">
            <KpiCustomerHeatmapTable
              title="Incidents By Customer"
              variant="slate"
              heatmap={heatmap}
              sortedRows={sortedRows}
              onToggleCustomerSort={() => setCustomerSortDesc((d) => !d)}
              maxCount={maxH}
              onCellDrill={onDrill}
            />
          </div>
          <div className="min-h-0 lg:col-span-4">
            <KpiIncidentCommComplianceDonuts
              ongoingCommRatePct={ongoingRate}
              ongoingCommTicketCount={totalSla}
              ongoingCompliantCount={ongoingCompliant}
              ongoingNonCompliantCount={ongoingNonCompliant}
              compliantCount={resolutionCompliant}
              nonCompliantCount={resolutionNonCompliant}
              onOngoingCompliantSliceDrill={() => openRate('incident_ongoing_met')}
              onOngoingNonCompliantSliceDrill={() => openRate('incident_ongoing_breached')}
              onOngoingCenterDrill={() => openRate('incident_ongoing')}
              onCompliantSliceDrill={() => openRate('incident_resolution_met')}
              onNonCompliantSliceDrill={() => openRate('incident_resolution_breached')}
              onResolutionCenterDrill={() => openRate('incident_resolution')}
              complianceHeaderText="Incident Resolution SLA"
            />
          </div>
        </div>
      </section>

      <section className="mb-6">
        <SectionLabel>Backlog &amp; weekly volume</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiBacklogBarChart
            title="Backlog By Age Groups"
            data={chartsData.backlogAge}
            onBucketClick={openBacklog}
          />
          <KpiIncidentsWeeklyLineChart volumeByWeek={volumeByWeek} onDrill={onDrill} />
        </div>
      </section>

      <section>
        <SectionLabel>Priority &amp; assignment group</SectionLabel>
        <div className="grid grid-cols-1 items-stretch gap-4 lg:grid-cols-2">
          <KpiIncidentAssignmentGroupComboChart
            rows={chartsData.incidentsByAssignmentGroup}
            onGroupClick={openAssignmentGroup}
          />
          <KpiPrioritySlaChart
            title="Incidents By Priority w/ SLA Breach %"
            priorityCounts={chartsData.priorityCounts}
            slaBreachPct={chartsData.slaBreachPct}
            onPriorityBarClick={openPriority}
          />
        </div>
      </section>
    </div>
  );
};

export default KpiIncidentDashboardTab;
